// IndemnityTrameDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Tooltip,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  SaveAlt as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { makeStyles } from '@material-ui/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import TrameService from '../../../services/TrameService';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '95%',
    maxWidth: '95%',
    width: '95%',
    height: '90%',
    maxHeight: '90%',
    margin: 'auto',
  },
  dialogContent: {
    padding: theme.spacing(3),
    overflowX: 'auto',
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 350px)',
    overflow: 'auto',
    borderRadius: theme.spacing(1),
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    padding: theme.spacing(1.5),
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  editableCell: {
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
      padding: theme.spacing(1, 1),
      minHeight: '40px',
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 0.5),
    },
    '& .MuiInputBase-multiline': {
      padding: theme.spacing(1),
    },
  },
  headerCell: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    padding: theme.spacing(1.5),
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    '&:first-child': {
      borderTopLeftRadius: theme.spacing(1),
    },
    '&:last-child': {
      borderTopRightRadius: theme.spacing(1),
    },
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  numericField: {
    '& input': {
      textAlign: 'right',
    },
  },
  actionCell: {
    position: 'sticky',
    left: 0,
    backgroundColor: 'white',
    zIndex: 1,
  },
  stickyHeaderCell: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    backgroundColor: theme.palette.primary.main,
  },
  stickyTableCell: {
    position: 'sticky',
    left: 0,
    backgroundColor: 'white',
    zIndex: 1,
    boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
  },
  scrollNote: {
    marginBottom: theme.spacing(2),
    animation: 'pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 0.6,
    },
    '50%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0.6,
    },
  },
  inputDialog: {
    padding: theme.spacing(3),
    minWidth: 400,
  },
  successDialog: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  successIcon: {
    fontSize: 60,
    color: '#4caf50',
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 60,
    color: '#f44336',
    marginBottom: theme.spacing(2),
  },
  downloadButton: {
    marginTop: theme.spacing(2),
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
}));

const IndemnityTrameDialog = ({ open, onClose, missionData }) => {
  const classes = useStyles();
  const [trameData, setTrameData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [missionnairesData, setMissionnairesData] = useState([]);
  
  // États pour le dialogue de saisie du numéro de pièce
  const [openPieceDialog, setOpenPieceDialog] = useState(false);
  const [pieceNumber, setPieceNumber] = useState('');
  const [pieceNumberError, setPieceNumberError] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  // États pour le dialogue de succès/erreur
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [excelFileUrl, setExcelFileUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);

  // Colonnes du fichier Excel - Action en premier
  const columns = [
    { key: 'actions', label: 'Actions', width: 100, type: 'actions', editable: false },
    { key: 'site', label: 'Site', width: 100, type: 'text', editable: true },
    { key: 'journal', label: 'Journal', width: 120, type: 'text', editable: true },
    { key: 'n_piece', label: 'N° Pièce', width: 180, type: 'text', editable: true },
    { key: 'date_comptable', label: 'Date Comptable', width: 150, type: 'date', editable: true },
    { key: 'date_piece', label: 'Date Pièce', width: 150, type: 'date', editable: true },
    { key: 'marche', label: 'Marché', width: 120, type: 'text', editable: true },
    { key: 'libelle', label: 'Libellé', width: 500, type: 'textarea', editable: true, multiline: true, rows: 3 },
    { key: 'debit_local', label: 'Débit local', width: 150, type: 'number', editable: true },
    { key: 'credit_local', label: 'Crédit local', width: 150, type: 'number', editable: true },
    { key: 'debit_devise', label: 'Débit Devise', width: 140, type: 'number', editable: true },
    { key: 'credit_devise', label: 'Crédit Devise', width: 140, type: 'number', editable: true },
    { key: 'debit_rapport', label: 'Débit Rapport', width: 140, type: 'number', editable: true },
    { key: 'credit_rapport', label: 'Crédit Rapport', width: 140, type: 'number', editable: true },
    { key: 'compte', label: 'Compte', width: 140, type: 'text', editable: true },
    { key: 'tiers', label: 'Tiers', width: 120, type: 'text', editable: true },
    { key: 'acti', label: 'Acti', width: 140, type: 'text', editable: true },
    { key: 'budget', label: 'Budget', width: 120, type: 'text', editable: true },
    { key: 'geo', label: 'Géo', width: 120, type: 'text', editable: true },
    { key: 'pcop', label: 'PCOP', width: 120, type: 'text', editable: true },
    { key: 'financement', label: 'Financement', width: 140, type: 'text', editable: true },
    { key: 'categorie', label: 'Catégorie', width: 120, type: 'text', editable: true },
    { key: 'ref_facture', label: 'REF facture', width: 180, type: 'text', editable: true },
    { key: 'devise', label: 'DEVISE', width: 120, type: 'text', editable: true },
    { key: 't', label: 'T', width: 120, type: 'text', editable: true },
  ];

  // Fonction pour formater la date au format YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Formater la date pour l'affichage (DD/MM/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  // Générer le numéro OM
  const generateOMNumber = (missionPlanningId) => {
    return `OM${String(missionPlanningId || '').padStart(6, '0')}G`;
  };

  // Récupérer les données des missionnaires depuis l'API
  useEffect(() => {
    if (open && missionData && missionData.mission_planning_id) {
      // Ouvrir le dialogue pour saisir le numéro de pièce
      setOpenPieceDialog(true);
      setPieceNumber('');
      setPieceNumberError('');
      setIsSaved(false);
      setTrameData([]);
    }
  }, [open, missionData]);

  // Générer les données de trame à partir des données API avec le numéro de pièce
  const generateTrameDataFromAPI = (missionnairesList, pieceNum) => {
    const newData = [];
    let debitTotal = 0;
    let creditTotal = 0;

    if (!missionData) return;

    const todayDate = getTodayDate();
    const omNumber = generateOMNumber(missionData.mission_planning_id);
    const siteValue = missionnairesList[0]?.site || '90';
    const lieu = missionData.lieufinal || missionData.lieu || 'ANTSIRABE';
    
    // Formater les dates
    const dateDebut = missionData.date_debut ? missionData.date_debut.split('T')[0] : todayDate;
    const dateFin = missionData.date_fin ? missionData.date_fin.split('T')[0] : todayDate;
    
    const dateDebutDisplay = formatDateForDisplay(dateDebut);
    const dateFinDisplay = formatDateForDisplay(dateFin);

    missionnairesList.forEach((missionnaire, index) => {
      const dernierPrenom = missionnaire.dernier_prenom || missionnaire.nom?.split(' ').pop() || 'MISSIONNAIRE';
      const missionTitle6mots = missionnaire.mission_title_6mots || missionData.mission_title || 'RENFORCEMENT DE CAPACITES';
      const montantIndemnite = parseInt(missionnaire.montant_indemnite, 10) || 0;
      
      // Libellé de base avec le numéro de pièce
      const libelleBase = `INDEMNITE DE MISSION ${pieceNum} ${dernierPrenom.toUpperCase()} DURANT ${missionTitle6mots} DU ${dateDebutDisplay} AU ${dateFinDisplay} A ${lieu}`;

      // Ligne 1: OD00 - Débit (Compte 625200)
      newData.push({
        id: `od00_${index}_1`,
        site: siteValue,
        journal: 'OD00',
        n_piece: pieceNum,
        date_comptable: todayDate,
        date_piece: todayDate,
        marche: missionData.reference || '',
        libelle: libelleBase,
        debit_local: montantIndemnite,
        credit_local: '',
        debit_devise: '',
        credit_devise: '',
        debit_rapport: '',
        credit_rapport: '',
        compte: '',
        tiers: '',
        acti: '',
        budget: '',
        geo: '',
        pcop: '',
        financement: '01',
        categorie: '2',
        ref_facture: '',
        devise: '',
        t: 'TG006',
      });
      debitTotal += montantIndemnite;

      // Ligne 2: OD00 - Crédit (Compte 421090)
      newData.push({
        id: `od00_${index}_2`,
        site: siteValue,
        journal: 'OD00',
        n_piece: pieceNum,
        date_comptable: todayDate,
        date_piece: todayDate,
        marche: missionData.reference || '',
        libelle: libelleBase,
        debit_local: '',
        credit_local: montantIndemnite,
        debit_devise: '',
        credit_devise: '',
        debit_rapport: '',
        credit_rapport: '',
        compte: '',
        tiers: '',
        acti: '',
        budget: '',
        geo: '',
        pcop: '',
        financement: '',
        categorie: '',
        ref_facture: '',
        devise: '',
        t: '',
      });
      creditTotal += montantIndemnite;

      // Ligne 3: 5890 - Débit
      newData.push({
        id: `5890_${index}_1`,
        site: siteValue,
        journal: '5890',
        n_piece: pieceNum,
        date_comptable: todayDate,
        date_piece: todayDate,
        marche: missionData.reference || '',
        libelle: libelleBase,
        debit_local: montantIndemnite,
        credit_local: '',
        debit_devise: '',
        credit_devise: '',
        debit_rapport: '',
        credit_rapport: '',
        compte: '',
        tiers: '',
        acti: '',
        budget: '',
        geo: '',
        pcop: '',
        financement: '01',
        categorie: '2',
        ref_facture: '',
        devise: '',
        t: 'TG006',
      });

      // Ligne 4: 5890 - Crédit
      newData.push({
        id: `5890_${index}_2`,
        site: siteValue,
        journal: '5890',
        n_piece: pieceNum,
        date_comptable: todayDate,
        date_piece: todayDate,
        marche: missionData.reference || '',
        libelle: `${libelleBase}`,
        debit_local: '',
        credit_local: montantIndemnite,
        debit_devise: '',
        credit_devise: '',
        debit_rapport: '',
        credit_rapport: '',
        compte: '',
        tiers: '',
        acti: '',
        budget: '',
        geo: '',
        pcop: '',
        financement: '',
        categorie: '',
        ref_facture: '',
        devise: '',
        t: '',
      });
    });

    setTrameData(newData);
    setTotalDebit(debitTotal);
    setTotalCredit(creditTotal);
    setLoading(false);
  };

  // Valider le numéro de pièce et charger les données
  const handleValidatePieceNumber = async () => {
    if (!pieceNumber.trim()) {
      setPieceNumberError('Le numéro de pièce est requis');
      return;
    }

    setLoading(true);
    setOpenPieceDialog(false);
    
    try {
      const result = await TrameService.getDetailstrame(missionData.mission_planning_id);
      if (result && result.data && result.data.length > 0) {
        setMissionnairesData(result.data);
        generateTrameDataFromAPI(result.data, pieceNumber);
      } else {
        console.error('Aucune donnée de missionnaire trouvée');
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      setLoading(false);
    }
  };

  // Exporter vers Excel (frontend)
  const exportToExcel = () => {
    const exportColumns = columns.filter(col => col.key !== 'actions');
    const exportData = trameData.map(row => {
      const exportRow = {};
      exportColumns.forEach(col => {
        let value = row[col.key] || '';
        exportRow[col.label] = value;
      });
      return exportRow;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const colWidths = exportColumns.map(col => ({ wch: Math.min(col.width / 7, 50) }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TRAME_COMPTABLE');

    const fileName = `TRAME_MISSION_${missionData?.reference || 'INFRA'}_${pieceNumber}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Enregistrer les données dans le backend
  const handleSaveTrame = async () => {
    if (!trameData.length) {
      setErrorMessage('Aucune donnée à enregistrer');
      setOpenErrorDialog(true);
      return;
    }

    setLoading(true);
    try {
      const saveData = {
        mission_planning_id: missionData.mission_planning_id,
        n_piece: pieceNumber,
        trame_data: trameData,
        total_debit: totalDebit,
        total_credit: totalCredit
      };

      const result = await TrameService.saveTrame(saveData);
      
      if (result && result.status) {
        setSuccessMessage('Trame enregistrée avec succès !');
        setExcelFileUrl(result.excel_file || null);
        setOpenSuccessDialog(true);
        setIsSaved(true);
      } else {
        setErrorMessage(result.message || 'Erreur lors de l\'enregistrement');
        setOpenErrorDialog(true);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      setErrorMessage(error.message || 'Erreur lors de l\'enregistrement de la trame');
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le fichier Excel depuis le backend
  const handleDownloadExcel = () => {
    if (excelFileUrl) {
          // Récupérer l'URL de base depuis .env
          const baseUrl = process.env.REACT_APP_API_URL_FILEPGI;
          const fullUrl = `${baseUrl}/${excelFileUrl}`;
          
          // Télécharger via un lien
          const link = document.createElement('a');
          link.href = fullUrl;
          link.download = excelFileUrl.split('/').pop(); 
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
    } else {
      exportToExcel();
    }
  };

  // Fermer le dialogue de succès et fermer le dialogue principal
  const handleCloseSuccessAndClose = () => {
    setOpenSuccessDialog(false);
    onClose();
  };

  const calculateTotals = (data) => {
    let debit = 0;
    let credit = 0;
    data.forEach(row => {
      if (row.debit_local && typeof row.debit_local === 'number') {
        debit += row.debit_local;
      } else if (row.debit_local && typeof row.debit_local === 'string') {
        debit += parseFloat(row.debit_local) || 0;
      }
      if (row.credit_local && typeof row.credit_local === 'number') {
        credit += row.credit_local;
      } else if (row.credit_local && typeof row.credit_local === 'string') {
        credit += parseFloat(row.credit_local) || 0;
      }
    });
    setTotalDebit(debit);
    setTotalCredit(credit);
  };

  const handleCellChange = (rowId, columnKey, value) => {
    setTrameData(prevData =>
      prevData.map(row => {
        if (row.id === rowId) {
          let newValue = value;
          const column = columns.find(col => col.key === columnKey);
          if (column?.type === 'number' && value !== '') {
            newValue = parseFloat(value) || 0;
          }
          return { ...row, [columnKey]: newValue };
        }
        return row;
      })
    );
    setTimeout(() => {
      calculateTotals(trameData);
    }, 100);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      site: '',
      journal: '',
      n_piece: pieceNumber,
      date_comptable: getTodayDate(),
      date_piece: getTodayDate(),
      marche: '',
      libelle: '',
      debit_local: '',
      credit_local: '',
      debit_devise: '',
      credit_devise: '',
      debit_rapport: '',
      credit_rapport: '',
      compte: '',
      tiers: '',
      acti: '',
      budget: '',
      geo: '',
      pcop: '',
      financement: '',
      categorie: '',
      ref_facture: '',
      devise: '',
      t: '',
    };
    setTrameData([...trameData, newRow]);
  };

  const handleDuplicateRow = (rowToDuplicate) => {
    const newRow = {
      ...rowToDuplicate,
      id: `duplicate_${Date.now()}`,
    };
    setTrameData([...trameData, newRow]);
  };

  const handleDeleteRow = (rowId) => {
    setTrameData(trameData.filter(row => row.id !== rowId));
    setTimeout(() => {
      calculateTotals(trameData);
    }, 100);
  };

  // Rendu de cellule éditable
  const renderEditableCell = (row, column) => {
    const value = row[column.key] || '';
    
    if (column.type === 'date') {
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => handleCellChange(row.id, column.key, e.target.value)}
          style={{
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            width: '100%',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
          }}
        />
      );
    }
    
    if (column.type === 'number') {
      return (
        <TextField
          value={value}
          onChange={(e) => handleCellChange(row.id, column.key, e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          type="number"
          placeholder="0"
          InputProps={{
            className: classes.editableCell,
            inputProps: { 
              step: 'any',
              style: { textAlign: 'right' }
            },
          }}
        />
      );
    }
    
    if (column.type === 'textarea') {
      return (
        <TextField
          value={value}
          onChange={(e) => handleCellChange(row.id, column.key, e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={column.rows || 2}
          placeholder=""
          InputProps={{
            className: classes.editableCell,
          }}
        />
      );
    }
    
    return (
      <TextField
        value={value}
        onChange={(e) => handleCellChange(row.id, column.key, e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        placeholder=""
        InputProps={{
          className: classes.editableCell,
        }}
      />
    );
  };

  return (
    <>
      {/* Dialogue pour saisir le numéro de pièce */}
      <Dialog 
        open={openPieceDialog} 
        onClose={() => {
          setOpenPieceDialog(false);
          onClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: '#1976d2', color: 'white' }}>
          📝 Saisie du numéro de pièce
        </DialogTitle>
        <DialogContent className={classes.inputDialog}>
          <Typography variant="body2" style={{ marginBottom: '20px', color: '#666' }}>
            Veuillez saisir le numéro de pièce pour générer la trame comptable.
          </Typography>
          <TextField
            fullWidth
            label="Numéro de pièce"
            variant="outlined"
            value={pieceNumber}
            onChange={(e) => {
              setPieceNumber(e.target.value);
              setPieceNumberError('');
            }}
            error={!!pieceNumberError}
            helperText={pieceNumberError}
            placeholder="Ex: OM2024001G"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenPieceDialog(false);
              onClose();
            }} 
            color="secondary"
            variant="contained"
            style={{ backgroundColor: '#f50057' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleValidatePieceNumber} 
            color="primary"
            variant="contained"
            style={{ backgroundColor: '#4caf50' }}
          >
            Générer la trame
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue principal de la trame */}
      <Dialog
        open={open && !openPieceDialog}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        PaperProps={{
          style: {
            margin: '20px',
            width: 'calc(100% - 40px)',
            maxWidth: 'none',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Élaboration de la trame comptable
              {missionData && (
                <Chip
                  label={missionData.reference}
                  size="small"
                  color="primary"
                  style={{ marginLeft: 10 }}
                />
              )}
              {pieceNumber && (
                <Chip
                  label={`Pièce: ${pieceNumber}`}
                  size="small"
                  color="secondary"
                  style={{ marginLeft: 10 }}
                />
              )}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Alert 
            severity="info" 
            icon={<InfoIcon />}
            className={classes.scrollNote}
            style={{ marginBottom: '16px' }}
          >
            <AlertTitle style={{ fontWeight: 'bold' }}>
              📜 Information importante
            </AlertTitle>
            <Typography variant="body2">
              <strong>👉 Veuillez faire défiler vers la droite (scroll horizontal) 👈</strong> pour visualiser l'intégralité des colonnes de la trame comptable.
              <br />
              Le tableau contient <strong>{columns.length - 1} colonnes</strong> de données et peut nécessiter un défilement horizontal pour accéder à toutes les informations.
            </Typography>
          </Alert>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress /> Chargement en cours .....
            </Box>
          ) : (
            <>
              {/* Résumé des informations */}
              {missionData && (
                <Paper className={classes.summaryCard} variant="outlined">
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Référence mission
                      </Typography>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {missionData.reference || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Objet
                      </Typography>
                      <Typography variant="body1">
                        {missionData.mission_title || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Lieu
                      </Typography>
                      <Typography variant="body1">
                        {missionData.lieufinal || missionData.lieu || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Date départ
                      </Typography>
                      <Typography variant="body1">
                        {missionData.date_depart ? missionData.date_depart : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Date retour
                      </Typography>
                      <Typography variant="body1">
                        {missionData.date_fin ? missionData.date_fin : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Débit / Crédit
                      </Typography>
                      <Typography variant="body1" style={{ 
                        color: Math.abs(totalDebit - totalCredit) < 1 ? '#4caf50' : '#f44336',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}>
                        {totalDebit.toLocaleString()} Ar / {totalCredit.toLocaleString()} Ar
                        {Math.abs(totalDebit - totalCredit) >= 1 && ` (Écart: ${Math.abs(totalDebit - totalCredit).toLocaleString()} Ar)`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Tableau des écritures */}
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          className={col.key === 'actions' ? classes.stickyHeaderCell : classes.headerCell}
                          style={{ 
                            minWidth: col.width, 
                            maxWidth: col.width,
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trameData.map((row) => (
                      <TableRow 
                        key={row.id} 
                        className={classes.tableRow}
                        hover
                      >
                        {columns.map((col) => (
                          <TableCell 
                            key={col.key} 
                            className={col.key === 'actions' ? classes.stickyTableCell : classes.tableCell}
                            style={{ 
                              minWidth: col.width,
                              maxWidth: col.width,
                            }}
                          >
                            {col.key === 'actions' ? (
                              <Box display="flex" gap={0.5} justifyContent="center">
                                <Tooltip title="Dupliquer la ligne">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDuplicateRow(row)}
                                    style={{ padding: 4 }}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer la ligne">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteRow(row.id)}
                                    style={{ padding: 4 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              renderEditableCell(row, col)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Box className={classes.actionButtons}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
            >
              Ajouter ligne
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSaveTrame}
              disabled={loading || !trameData.length}
              startIcon={<SaveIcon />}
            >
              Enregistrer
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Fermer
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialogue de succès avec proposition de téléchargement */}
      <Dialog
        open={openSuccessDialog}
        onClose={handleCloseSuccessAndClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: '#4caf50', color: 'white', textAlign: 'center' }}>
          Succès !
        </DialogTitle>
        <DialogContent className={classes.successDialog}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#2e7d32' }}>
            {successMessage}
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
            La trame a été enregistrée avec succès 
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadExcel}
            className={classes.downloadButton}
            fullWidth
          >
            Télécharger le fichier Excel
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessAndClose} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'erreur */}
      <Dialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: '#f44336', color: 'white', textAlign: 'center' }}>
          Erreur
        </DialogTitle>
        <DialogContent className={classes.successDialog}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#c62828' }}>
            Une erreur est survenue
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IndemnityTrameDialog;